import type { Query } from '@tanstack/react-query';

import { normalizeId } from 'utils/ids';

import type { TreeCacheScope } from '../types';

/**
 * Every query key and every cache predicate for the decomposition tree. This is the ONLY module
 * that knows the key layout, so nothing else indexes into a key or repeats the sentinel below.
 *
 * Ids are normalized here (`normalizeId`), which is what lets callers pass whatever they hold —
 * a route segment, a SignalR payload id, a cached node's id — without normalizing first. Two keys
 * for the same node are therefore always equal regardless of casing or padding.
 */
const PREFIX = 'tree';

/** Stands in for "the root list" so the sticky-top key has a fixed arity. */
const ROOT_LIST = '__root__';

type ListKind = 'roots' | 'children' | 'sticky-top';

function scopePrefix(scope: TreeCacheScope) {
  return [
    PREFIX,
    normalizeId(scope.projectId),
    normalizeId(scope.templateNodeId),
    scope.proposalId ? normalizeId(scope.proposalId) : undefined
  ] as const;
}

/** Reads a key's `[prefix, projectId, templateNodeId, proposalId, kind, parentDataNodeId]` shape. */
function readKey(key: readonly unknown[]) {
  const at = (index: number) => (typeof key[index] === 'string' ? (key[index] as string) : undefined);
  return {
    isTreeKey: key[0] === PREFIX,
    projectId: at(1),
    templateNodeId: at(2),
    proposalId: at(3),
    kind: at(4),
    parentDataNodeId: at(5)
  };
}

function isInScope(key: readonly unknown[], scope: TreeCacheScope): boolean {
  const parsed = readKey(key);
  if (!parsed.isTreeKey) return false;
  if (parsed.projectId !== normalizeId(scope.projectId)) return false;
  if (parsed.templateNodeId !== normalizeId(scope.templateNodeId)) return false;
  return parsed.proposalId === (scope.proposalId ? normalizeId(scope.proposalId) : undefined);
}

function ofKind(scope: TreeCacheScope, ...kinds: ListKind[]) {
  return (query: Query): boolean => {
    const key = query.queryKey;
    if (!isInScope(key, scope)) return false;
    const kind = readKey(key).kind;
    return kinds.some((k) => k === kind);
  };
}

export const treeQueryKeys = {
  roots: (scope: TreeCacheScope) => [...scopePrefix(scope), 'roots'] as const,

  children: (scope: TreeCacheScope, parentDataNodeId: string) =>
    [...scopePrefix(scope), 'children', normalizeId(parentDataNodeId)] as const,

  /**
   * Nodes optimistically pinned to the very top of a list (the roots list, or one parent's
   * children) after a successful add. Lives separately from the paginated data cache so refetches
   * and invalidations of the underlying pages never wipe the pin — that is what stops a
   * freshly-added node from jumping into its natural sort position when the server returns it.
   *
   * A pin owns POSITION, not data: once the node comes back in a page, the consumer renders the
   * server copy at the pinned position (mergeNodes.ts), so a pin can't freeze stale fields for the
   * rest of the session. The pinned payload only renders while the server hasn't returned the node.
   *
   * Pass `parentDataNodeId = undefined` for the roots list.
   */
  stickyTop: (scope: TreeCacheScope, parentDataNodeId?: string) =>
    [...scopePrefix(scope), 'sticky-top', parentDataNodeId ? normalizeId(parentDataNodeId) : ROOT_LIST] as const,

  /** Any tree query in this scope, whichever list it holds. */
  inScope: (scope: TreeCacheScope) => (query: Query) => isInScope(query.queryKey, scope),

  /** The paginated lists: roots and every parent's children. */
  pages: (scope: TreeCacheScope) => ofKind(scope, 'roots', 'children'),

  /** Every sticky-top list, root included. */
  sticky: (scope: TreeCacheScope) => ofKind(scope, 'sticky-top'),

  /** Sticky-top lists belonging to a parent — i.e. every pin except a root pin. */
  nonRootSticky: (scope: TreeCacheScope) => {
    const isSticky = ofKind(scope, 'sticky-top');
    return (query: Query): boolean => isSticky(query) && readKey(query.queryKey).parentDataNodeId !== ROOT_LIST;
  },

  /** Every tree query of a project, across templates and proposals. Used when leaving the tree. */
  inProject: (projectId: string) => (query: Query) => {
    const parsed = readKey(query.queryKey);
    return parsed.isTreeKey && parsed.projectId === normalizeId(projectId);
  }
};
